import {
  ComponentBox,
  ComponentBoxHeaderFooter,
  ExportButton,
  GeneratedLayout,
  LoadingSpinner,
  StateId,
  utils,
} from '@wallaby-ui/core';
import classNames from 'classnames';
import * as React from 'react';
import {useMemo} from 'react';
import {defineMessages, useIntl} from 'react-intl';

import styles from './ReportsBox.less';
import {ReportsList} from './ReportsList';
import {ReportsListItem} from './ReportsListItem';
import ExportLimitReachedContainer from 'src/js/containers/filter_and_search/components/ExportLimitReachedContainer';
import {ReportsConfig} from 'src/js/model/reports_config';
import {useAppSelector} from 'src/js/store_hooks';

const I18N = defineMessages({
  HELP: {
    id: 'reports.help',
    defaultMessage: 'This box is showing the reports.',
    description: 'help text for reports box',
  },
  EXPORT_REPORT: {
    id: 'reports.export.button',
    defaultMessage: 'Export report',
    description: 'title text for export button',
  },
});

interface OwnProps {
  caseType?: string;
  filterAndSearchStateId: StateId;
  handleExport?: () => void;
  isExportingCounter: number;
  isLoading: boolean;
  reportId: string;
  reportsStateId: StateId;
  data: Record<string, any>;
  disableExport: boolean;
}

type Props = OwnProps;

export const ReportsBox: React.FC<Props> = (props) => {
  const {
    // TODO-JK: refactor and remove caseType
    caseType = 'table',
    filterAndSearchStateId,
    handleExport,
    reportId,
    reportsStateId,
    isExportingCounter = 0,
    isLoading,
    data,
    disableExport,
  } = props;
  const intl = useIntl();

  const reportsConfigRaw = useAppSelector(
    (state) => state[reportsStateId].reportsConfig
  );

  const reportsConfig = useMemo(() => {
    return new ReportsConfig(reportsConfigRaw);
  }, [reportsConfigRaw]);

  const titleText =
    reportsConfig.isLoaded() && reportsConfig.getCaption(caseType)
      ? utils.TranslateUtils.translate(
          {id: reportsConfig.getCaption(caseType)},
          intl
        )
      : '';
  const helpText =
    reportsConfig.isLoaded() && reportsConfig.getHelpMessage()
      ? {id: reportsConfig.getHelpMessage()}
      : I18N.HELP;

  return (
    <div className={styles.dlReportsBox}>
      <div
        className={classNames(
          'dlLoadingSpinnerContainer',
          'dlFlexBoxContainerInContainer',
          {
            dlLoadingSpinnerParent:
              isLoading || isExportingCounter > 0 || !reportsConfig.isLoaded(),
          }
        )}
      >
        <LoadingSpinner
          isVisible={
            isLoading || isExportingCounter > 0 || !reportsConfig.isLoaded()
          }
        />
        <ExportLimitReachedContainer stateId={filterAndSearchStateId} />
        <ComponentBoxHeaderFooter
          helpText={utils.TranslateUtils.translate(helpText, intl)}
          titleText={titleText}
        >
          <span className={styles.dlReportsBoxHeaderContent}>
            {handleExport && !disableExport && (
              <ExportButton
                onExport={handleExport}
                isDisabled={disableExport || isExportingCounter > 0}
                titleText={I18N.EXPORT_REPORT.id}
              />
            )}
          </span>
        </ComponentBoxHeaderFooter>
        <ComponentBox>
          {reportsConfig &&
          reportsConfig.isLoaded() &&
          reportsConfig.getGeneratedLayout(caseType) &&
          reportsConfig.getGeneratedLayout(caseType).getComponents().length >
            0 ? (
            <GeneratedLayout
              layoutConfig={reportsConfig.getGeneratedLayout(caseType)}
              dataMap={reportsConfig.getReportsMap()}
              dataComponent={ReportsListItem}
              dataPropName={'reportConfig'}
              data={data}
              reportId={reportId}
            />
          ) : (
            <ReportsList
              caseType={caseType}
              reportId={reportId}
              data={data}
              reportsConfig={reportsConfig}
            />
          )}
        </ComponentBox>
      </div>
    </div>
  );
};

ReportsBox.displayName = 'ReportsBox';
